import { useQuery } from '@tanstack/react-query'
import { referralService } from '../services/referralService'
import { useAuth } from '../contexts/AuthContext'
import { formatDate } from '../utils/locale'
import './ReferralOverview.css'

interface ReferralOverviewProps {
  referringProviderId?: string
}

export function ReferralOverview({ referringProviderId }: ReferralOverviewProps) {
  const { user } = useAuth()
  const providerId = referringProviderId || user?.id

  const { data: overview, isLoading, error } = useQuery({
    queryKey: ['referrals', 'overview', providerId],
    queryFn: () => referralService.getOverview(providerId),
  })

  if (isLoading) {
    return <div className="referral-overview-loading">Loading overview...</div>
  }

  if (error || !overview) {
    return <div className="referral-overview-error">Error loading overview</div>
  }

  return (
    <div className="referral-overview">
      <h2>Referral Overview</h2>

      <div className="overview-stats-grid">
        <div className="stat-card">
          <div className="stat-value">{overview.total}</div>
          <div className="stat-label">Total Referrals</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{overview.byStatus.draft}</div>
          <div className="stat-label">Draft</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{overview.byStatus.sent}</div>
          <div className="stat-label">Sent</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{overview.byStatus.acknowledged}</div>
          <div className="stat-label">Acknowledged</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{overview.byStatus.completed}</div>
          <div className="stat-label">Completed</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{overview.pendingFollowUps}</div>
          <div className="stat-label">Pending Follow-ups</div>
        </div>
      </div>

      <div className="overview-sections">
        <div className="overview-section">
          <h3>By Priority</h3>
          <div className="priority-breakdown">
            <div className="breakdown-item">
              <span className="breakdown-label">Routine:</span>
              <span className="breakdown-value">{overview.byPriority.routine}</span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">Urgent:</span>
              <span className="breakdown-value">{overview.byPriority.urgent}</span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">Emergency:</span>
              <span className="breakdown-value">{overview.byPriority.emergency}</span>
            </div>
          </div>
        </div>

        {Object.keys(overview.bySpecialty).length > 0 && (
          <div className="overview-section">
            <h3>By Specialty</h3>
            <div className="specialty-breakdown">
              {Object.entries(overview.bySpecialty)
                .sort(([, a], [, b]) => b - a)
                .map(([specialty, count]) => (
                  <div key={specialty} className="breakdown-item">
                    <span className="breakdown-label">{specialty}:</span>
                    <span className="breakdown-value">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {Object.keys(overview.byProvider).length > 0 && (
          <div className="overview-section">
            <h3>By Provider</h3>
            <div className="provider-breakdown">
              {Object.entries(overview.byProvider)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 10)
                .map(([provider, count]) => (
                  <div key={provider} className="breakdown-item">
                    <span className="breakdown-label">{provider}:</span>
                    <span className="breakdown-value">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {overview.recentReferrals.length > 0 && (
        <div className="overview-section">
          <h3>Recent Referrals</h3>
          <div className="recent-referrals-list">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Patient</th>
                  <th>Referred To</th>
                  <th>Priority</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {overview.recentReferrals.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDate(r.referralDate)}</td>
                    <td>{r.patientId}</td>
                    <td>{r.referredToProviderName}</td>
                    <td>
                      <span className={`priority-badge priority-${r.priority}`}>
                        {r.priority}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${r.status}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
